import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import API from "@/lib/api";
import { Loader2, CheckCircle, XCircle, ArrowRight, RotateCcw, Sparkles, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const SCORE_COLORS = {
  A: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", ring: "ring-green-500" },
  B: { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400", ring: "ring-blue-500" },
  C: { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400", ring: "ring-yellow-500" },
  D: { bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400", ring: "ring-orange-500" },
  F: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", ring: "ring-red-500" },
};

export function EvaluateStep({ promptData, onAccept, onBack }) {
  const [evaluating, setEvaluating] = useState(false);
  const [evaluation, setEvaluation] = useState(promptData?.evaluation || null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(promptData?.master_prompt || "");

  useEffect(() => {
    if (promptData?.evaluation) {
      setEvaluation(promptData.evaluation);
    }
  }, [promptData?.evaluation]);

  useEffect(() => {
    setEditValue(promptData?.master_prompt || "");
  }, [promptData?.master_prompt]);

  const handleEvaluate = async () => {
    if (!promptData?.id) return;
    setEvaluating(true);
    try {
      const { data } = await API.post(`/prompts/${promptData.id}/evaluate`);
      setEvaluation(data);
    } catch (e) {
      toast.error("Evaluation failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setEvaluating(false);
    }
  };

  const handleCopy = async () => {
    const text = promptData?.master_prompt || "";
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSaveEdit = async () => {
    if (!promptData?.id) return;
    try {
      await API.put(`/prompts/${promptData.id}`, { content: editValue });
      setEditing(false);
      toast.success("Master prompt updated");
      // Trigger re-evaluation
      setEvaluation(null);
    } catch (e) {
      toast.error("Save failed");
    }
  };

  // Auto-evaluate if no evaluation exists
  useEffect(() => {
    if (promptData?.id && promptData?.master_prompt && !evaluation && !evaluating) {
      handleEvaluate();
    }
  }, [promptData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const masterPrompt = promptData?.master_prompt || "";
  const score = evaluation?.score || 0;
  const grade = evaluation?.grade || "?";
  const colors = SCORE_COLORS[grade] || SCORE_COLORS.C;
  const checklist = evaluation?.checklist || [];
  const suggestions = evaluation?.suggestions || [];
  const summary = evaluation?.summary || "";
  const passedCount = checklist.filter((c) => c.passed).length;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="evaluate-step">
      {/* Master Prompt Display */}
      <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)]">
        <div className="px-5 py-3 border-b border-[var(--pf-border)] flex items-center justify-between">
          <div>
            <h3 className="font-heading text-base font-bold text-[var(--pf-text)]">Master Prompt</h3>
            <p className="text-xs text-[var(--pf-text-secondary)] mt-0.5">This is the prompt you'd paste into your build platform to initiate development.</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={handleCopy} data-testid="eval-copy-master" className="rounded-none h-7 px-2 text-xs hover:bg-[var(--pf-surface)]">
              {copied ? <Check className="w-3 h-3 text-[#16A34A]" /> : <Copy className="w-3 h-3" />}
            </Button>
            {!editing && (
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="eval-edit-master" className="rounded-none h-7 px-2 text-xs hover:bg-[var(--pf-surface)] text-[var(--pf-accent)]">
                Edit
              </Button>
            )}
          </div>
        </div>
        <div className="p-5">
          {editing ? (
            <div className="space-y-3">
              <Textarea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none font-mono text-sm min-h-[250px]"
                rows={12}
              />
              <div className="flex items-center gap-2">
                <Button onClick={handleSaveEdit} className="bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-8 px-3 text-xs">Save & Re-evaluate</Button>
                <Button variant="ghost" onClick={() => { setEditValue(masterPrompt); setEditing(false); }} className="rounded-none h-8 px-3 text-xs">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="bg-[var(--pf-surface)] border border-[var(--pf-border)] p-4 font-mono text-sm leading-relaxed text-[var(--pf-text)] whitespace-pre-wrap max-h-[400px] overflow-y-auto" data-testid="eval-master-content">
              {masterPrompt || "No master prompt generated yet."}
            </div>
          )}
        </div>
      </div>

      {/* Evaluation Results */}
      {evaluating ? (
        <div className="border border-[var(--pf-border)] p-12 text-center" data-testid="eval-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--pf-accent)] mx-auto mb-3" />
          <p className="text-sm text-[var(--pf-text-secondary)]">Evaluating prompt quality...</p>
        </div>
      ) : evaluation ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Score Card */}
          <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-5" data-testid="eval-score-card">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-3">Quality Score</p>
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 flex items-center justify-center ${colors.bg} ring-2 ${colors.ring}`}>
                <span className={`font-heading text-2xl font-bold ${colors.text}`}>{score}</span>
              </div>
              <div>
                <span className={`text-3xl font-heading font-bold ${colors.text}`}>{grade}</span>
                <p className="text-xs text-[var(--pf-text-secondary)] mt-0.5">{passedCount}/{checklist.length} checks passed</p>
              </div>
            </div>
            {summary && <p className="text-xs text-[var(--pf-text-secondary)] mt-3 leading-relaxed">{summary}</p>}
            <Button variant="outline" size="sm" onClick={handleEvaluate} disabled={evaluating} data-testid="eval-re-evaluate" className="rounded-none border-[var(--pf-border)] h-7 text-[11px] mt-3 w-full hover:border-[var(--pf-accent)]">
              <RotateCcw className="w-3 h-3 mr-1" />Re-evaluate
            </Button>
          </div>

          {/* Checklist */}
          <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-5" data-testid="eval-checklist">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-3">Evaluation Checklist</p>
            <div className="space-y-2">
              {checklist.map((item, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`eval-check-${i}`}>
                  {item.passed
                    ? <CheckCircle className="w-3.5 h-3.5 text-[#16A34A] mt-0.5 shrink-0" />
                    : <XCircle className="w-3.5 h-3.5 text-[#DC2626] mt-0.5 shrink-0" />}
                  <div>
                    <span className="text-xs font-medium text-[var(--pf-text)]">{item.label}</span>
                    {item.detail && <p className="text-[10px] text-[var(--pf-text-muted)] mt-0.5">{item.detail}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Suggestions */}
          <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)] p-5" data-testid="eval-suggestions">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-3">Improvement Suggestions</p>
            {suggestions.length > 0 ? (
              <div className="space-y-2">
                {suggestions.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 border-l-2 border-[var(--pf-accent)] pl-2.5">
                    <Sparkles className="w-3 h-3 text-[var(--pf-accent)] mt-0.5 shrink-0" />
                    <p className="text-xs text-[var(--pf-text)] leading-relaxed">{s}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[var(--pf-text-muted)]">No suggestions — prompt looks solid.</p>
            )}
          </div>
        </div>
      ) : null}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={onBack} data-testid="eval-back" className="rounded-none border-[var(--pf-border)] h-9 px-4 text-xs">
          Back to Clarify
        </Button>
        <Button
          onClick={() => onAccept(promptData)}
          data-testid="eval-accept"
          className="bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-10 px-5 text-sm font-medium"
        >
          Accept & Generate Blocks <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
